import React, { useState } from 'react';
import { CheckCircle2, XCircle } from 'lucide-react';
import { computeClaimId, spokenCode, CLAIM_TYPES } from '@/lib/claimRecords';
import vectors from '@/lib/claimRecordVectors.json';

const EMPTY = { principal: '', merchant: '', orderRef: '', claimType: 'refund', claimNumber: '1' };
const inputCls = 'mt-1.5 w-full rounded-xl border border-white/10 bg-[#0a0a0a] p-2.5 font-mono text-xs text-gray-200 focus:border-amber-500/60 focus:outline-none';

function Input({ name, label, form, set }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-widest text-gray-400">{label}</span>
      <input value={form[name]} onChange={(e) => set({ ...form, [name]: e.target.value })} spellCheck={false} className={inputCls} data-testid={`claim-input-${name}`} />
    </label>
  );
}

function Result({ result }) {
  if (!result) return null;
  if (result.error) return <p className="text-sm text-red-300" data-testid="claim-error">{result.error}</p>;
  const ok = result.expected ? result.matches : true;
  return (
    <div className="space-y-3" data-testid="claim-result">
      <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
        <div className="text-xs uppercase tracking-widest text-gray-500 mb-1">Spoken code</div>
        <div className="font-mono text-3xl tracking-[0.3em] text-amber-200" data-testid="claim-spoken-code">{result.code}</div>
        <div className="text-xs uppercase tracking-widest text-gray-500 mt-4 mb-1">Claim ID</div>
        <div className="font-mono text-xs text-gray-300 break-all" data-testid="claim-id-output">{result.claimId}</div>
      </div>
      {result.expected && (
        <div className={`flex items-center gap-2 text-sm ${ok ? 'text-emerald-300' : 'text-red-300'}`} data-testid="claim-vector-match">
          {ok ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
          {ok ? 'Matches the published test vector.' : 'Does not match the published test vector.'}
        </div>
      )}
    </div>
  );
}

export function ClaimIdPanel() {
  const [form, setForm] = useState(EMPTY);
  const [expected, setExpected] = useState(null);
  const [result, setResult] = useState(null);
  const set = (f) => { setForm(f); setExpected(null); setResult(null); };
  const load = (c) => { setForm({ ...c.input, claimNumber: String(c.input.claimNumber) }); setExpected(c.expected); setResult(null); };
  const run = () => {
    try {
      const claimId = computeClaimId(form);
      const code = spokenCode(claimId);
      setResult({ claimId, code, expected, matches: expected && expected.claimId === claimId && expected.spokenCode === code });
    } catch (e) {
      setResult({ error: e.message });
    }
  };
  return (
    <section className="rounded-2xl border border-white/10 bg-white/[0.02] p-6" data-testid="claim-id-panel">
      <h2 className="text-lg font-semibold mb-1">1. Compute a claim ID and spoken code</h2>
      <p className="text-sm text-gray-400 mb-4">The same five inputs always give the same claim ID, so a repeated request folds into one case.</p>
      <div className="flex flex-wrap items-center gap-2 mb-5" data-testid="claim-examples">
        <span className="text-xs text-gray-500 mr-1">Load example:</span>
        {vectors.claims.map((c) => (
          <button key={c.id} type="button" onClick={() => load(c)} className="rounded-full border border-white/15 px-3 py-1 text-xs text-gray-200 hover:border-amber-400/60 hover:text-white transition-colors" data-testid={`claim-load-${c.id}`}>{c.id}</button>
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-4">
          <Input name="principal" label="Principal (0x address)" form={form} set={set} />
          <Input name="merchant" label="Merchant domain (lowercase)" form={form} set={set} />
          <Input name="orderRef" label="Order reference" form={form} set={set} />
          <div className="grid grid-cols-2 gap-4">
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-gray-400">Claim type</span>
              <select value={form.claimType} onChange={(e) => set({ ...form, claimType: e.target.value })} className={inputCls} data-testid="claim-input-claimType">
                {CLAIM_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </label>
            <Input name="claimNumber" label="Claim number" form={form} set={set} />
          </div>
          <button type="button" onClick={run} disabled={!form.principal.trim()} className="rounded-full bg-white text-black px-6 py-2.5 text-sm font-semibold hover:bg-gray-200 transition-colors disabled:opacity-40" data-testid="claim-compute-button">Compute in my browser</button>
        </div>
        <Result result={result} />
      </div>
    </section>
  );
}
